import { ReactElement, useEffect, useState } from 'react';
import { useFormContext, FormProvider } from 'react-hook-form';
import { HiOutlineCheckCircle } from 'react-icons/hi2';
import toast from 'react-hot-toast';
import { SessionAnalysisType, SessionCatalog, SessionStatus } from '../../../../types/SessionAnalysisType';
import Button from '../../../../components/Button';
import FormInput from '../../../../components/FormInput';
import FormEntry from '../../../../components/FormEntry';
import ParentSessionSelector from '../../../../components/ParentSessionSelector';
import VelodromeRouteSelector from '../../../../components/VelodromeRouteSelector';
import OptionSection from './OptionSection';

interface ChangeSessionTypeSectionProps {
  sessionId: string;
  session: any;
  updateSession: any;
  invalidateSession: () => Promise<void>;
}

export default function ChangeSessionTypeSection({
  sessionId,
  session,
  updateSession,
  invalidateSession,
}: ChangeSessionTypeSectionProps): ReactElement {
  const formMethods = useFormContext();
  const [sessionTypeChanged, setSessionTypeChanged] = useState(false);
  const [isSavingSessionType, setIsSavingSessionType] = useState(false);

  // Watch for changes to analysis_type or parent_id to show/hide save button
  useEffect(() => {
    if (session) {
      const subscription = formMethods.watch((value, { name }) => {
        if (name === 'analysis_type' || name === 'parent_id') {
          const analysisTypeChanged = Number(value.analysis_type) !== session.analysis_type;
          const parentIdChanged = value.parent_id !== session.parent_id;
          setSessionTypeChanged(analysisTypeChanged || parentIdChanged);
        }
      });

      return () => subscription.unsubscribe();
    }
  }, [formMethods, session]);

  const watchedAnalysisType = formMethods.watch('analysis_type');
  const currentAnalysisType = Number(watchedAnalysisType || session?.analysis_type);

  const showParentSessionDropdown = currentAnalysisType === SessionAnalysisType.TRAVERSE_TYRE_COMPARISON;

  const isVelodromeSession =
    currentAnalysisType === SessionAnalysisType.LOOP_VELODROME_INDOORS ||
    currentAnalysisType === SessionAnalysisType.LOOP_VELODROME_OUTDOORS;

  const handleSaveSessionType = async () => {
    try {
      setIsSavingSessionType(true);
      const formValues = formMethods.getValues();

      // Only save analysis_type and parent_id
      const sessionTypeData: any = {
        analysis_type: Number(formValues.analysis_type),
      };

      // Include parent_id if it exists and is not empty
      if (formValues.parent_id && formValues.parent_id !== '') {
        sessionTypeData.parent_id = formValues.parent_id;
      }

      await updateSession.mutateAsync(sessionTypeData);

      setSessionTypeChanged(false);
      toast.success('Session type updated successfully');

      // Invalidate session to refresh data
      invalidateSession();
    } catch (e) {
      console.error(e);
      toast.error('Error updating session type');
    } finally {
      setIsSavingSessionType(false);
    }
  };

  const currentTypeName = SessionCatalog[currentAnalysisType]?.[0] as string | undefined;

  return (
    <OptionSection
      title="Session Type"
      status={currentTypeName && <span className="cr-text-sm cr-text-gray-500">{currentTypeName}</span>}
    >
      {/* Session Test Type Selection */}
      <div className="cr-mb-4 cr-flex cr-flex-col cr-rounded-lg cr-bg-white cr-px-4 cr-pb-4 cr-pt-3 cr-shadow-md md:cr-w-[640px]">
        <FormEntry label="Session Test Type">
          <FormInput
            fieldName="analysis_type"
            required="Session Test Type is Required"
            options={Object.entries(SessionCatalog)
              .map(([analysisType, [name, sortOrder, status]]) => ({
                id: Number(analysisType),
                name: name as string,
                sortOrder: sortOrder as number,
                status: status as SessionStatus,
                disabled: status === SessionStatus.COMING_SOON || status === SessionStatus.IN_DEVELOPMENT,
              }))
              .sort((a, b) => a.sortOrder - b.sortOrder)}
            optionLabelFn={(option) => {
              let label = option.name;
              if (option.status === SessionStatus.BETA) {
                label += ' - Beta Phase';
              } else if (option.status === SessionStatus.COMING_SOON) {
                label += ' - Coming Soon';
              } else if (option.status === SessionStatus.IN_DEVELOPMENT) {
                label += ' - Coming Soon';
              }
              return label;
            }}
            emptyOption="Select Session Test Type"
            customClass="cr-w-full md:cr-w-[420px]"
          />
        </FormEntry>
      </div>

      {/* Parent Session Selection for TRAVERSE_TYRE_COMPARISON */}
      <FormProvider {...formMethods}>
        <ParentSessionSelector
          showDropdown={showParentSessionDropdown}
          className="cr-mb-4"
          currentSessionId={sessionId}
        />
      </FormProvider>

      {/* Velodrome Route Selector */}
      {isVelodromeSession && (
        <VelodromeRouteSelector
          analysisType={currentAnalysisType}
          selectedRouteId={session?.route_id}
          onRouteSelect={async (routeId) => {
            try {
              await updateSession.mutateAsync({ route_id: routeId });
              toast.success(routeId ? 'Velodrome route selected' : 'Velodrome route deselected');
              await invalidateSession();
            } catch (e) {
              console.error(e);
              toast.error('Failed to update velodrome route');
            }
          }}
          className="cr-mb-4"
        />
      )}

      {/* Save Button */}
      {sessionTypeChanged && (
        <div className="cr-flex cr-justify-end">
          <Button
            onClick={handleSaveSessionType}
            disabled={isSavingSessionType}
            icon={<HiOutlineCheckCircle className="cr-h-5 cr-w-5 cr-stroke-[2.2px]" />}
            normalCase
            className="cr-h-11 cr-font-semibold"
          >
            {isSavingSessionType ? 'Saving...' : 'Save'}
          </Button>
        </div>
      )}
    </OptionSection>
  );
}
